import { useMemo } from "react";
import {
	SelectWithLabel,
	type SelectOption,
} from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { PRESET_OPTIONS } from "./quote-table-constants";
import type { QuoteTemplate } from "../../api/quote-templates-api";

export interface TemplateSelectorProps {
	isEditMode: boolean;
	selectedPreset: string;
	templates: QuoteTemplate[];
	onPresetChange: (presetKey: string) => void;
}

/**
 * Preset / saved-template picker shown at the top of CreateQuoteTableModal in
 * create mode. Returns null in edit mode. The parent owns "unsaved changes"
 * confirmation logic — this component fires onPresetChange directly.
 */
export const TemplateSelector = ({
	selectedPreset,
	onPresetChange,
	templates,
	isEditMode,
}: TemplateSelectorProps) => {
	if (isEditMode) {
		return null;
	}

	const options: SelectOption[] = useMemo(() => {
		const builtIn: SelectOption[] = PRESET_OPTIONS.map((opt) => ({
			label: opt.label,
			value: opt.value,
		}));
		const userTemplates: SelectOption[] = templates.map((tmpl) => ({
			label: `My Template: ${tmpl.name}`,
			value: `template_${tmpl.id}`,
		}));
		return [...builtIn, ...userTemplates];
	}, [templates]);

	const selectedOption =
		options.find((o) => o.value === selectedPreset) || options[0];

	return (
		<SelectWithLabel
			label="Template"
			name="quote-table-template"
			options={options}
			value={selectedOption}
			onChange={(opt) => {
				const selected = opt as SelectOption | null;
				onPresetChange(selected?.value ?? "");
			}}
			isClearable={false}
			isSearchable={false}
		/>
	);
};

export default TemplateSelector;
